import { Injectable } from "@angular/core";
import { FinancialClientStatementReportDTO } from "@gipi-financial/client/models/dto/client-statement-report.dto";
import { FinancialCompanyService } from "@gipi-financial/company/services/company.service";
import { OAuthUser } from "@gipi-financial/user/models/user.model";
import { CustomAuthenticationService } from "@gipi-shared/services/custom-authentication.service";
import { ArrayUtil, CurrencyUtil, DateUtil, GIPIAbstractService, ObjectUtil, StringUtil, TokenDTO } from "@gipisistemas/ng-core";
import jsPDF from 'jspdf';
import * as JsAutoTable from 'jspdf-autotable';
import * as moment from 'moment';
import { RegistrationClient } from "../models/client.model";

export interface PdfClientFileExtractModel {
    clientName: string,
    clientCpfOrCnpj: string,
    clientStatementList: FinancialClientStatementReportDTO[];
    // showAddress: boolean;
}

@Injectable({ providedIn: 'root' })
export class PdfClientFileExtractService extends GIPIAbstractService {

    private _user: string = '';
    private _company: string = '';

    constructor(
        private _authenticationService: CustomAuthenticationService,
        private _companyService: FinancialCompanyService,
    ) {
        super('', '');

        // this._getUserLogged();
        this._getCompanyLogged();
    }

    public exportPdf(options: PdfClientFileExtractModel): void {
        if (ObjectUtil.isNull(options)) {
            return;
        }


        const doc = new jsPDF({ orientation: 'portrait', putOnlyUsedFonts: true, });

        const separatorEqual = '='.repeat(90);
        const separatorDash = '- '.repeat(90);

        // Cabeçalho
        doc.setFont('courier', 'normal');
        doc.setFontSize(10);
        doc.text('======================================== EXTRATO =========================================', 10, 10);
        doc.setFont('courier', 'bold').text(this._company, 10, 14);
        doc.setFont('courier', 'normal').text(`Emissão: ${moment().format('DD/MM/YYYY - HH:mm:ss')}`, 200, 14, { align: 'right' });
        doc.setFont('courier', 'normal').text(`Cliente: ${options.clientName}`, 10, 18);
        doc.setFont('courier', 'normal').text(`CPF/CNPJ: ${options.clientCpfOrCnpj}`, 10, 22);

        // if (options.showAddress) {
        //     doc.text(`Endereço: ${clientInfo.street}`, 10, 26);
        //     doc.text(`Cidade: ${clientInfo.city}     Cep: ${clientInfo.zipCode}`, 10, 30);
        //     doc.text(`Bairro: ${clientInfo.neighborhood}`, 10, 34);
        //     doc.text(`Fone(s): ${clientInfo.phones}`, 10, 38);
        // }

        // doc.text(separatorEqual, 10, options.showAddress ? 42 : 26);
        doc.text(separatorEqual, 10, 26);

        // Tabela
        JsAutoTable.autoTable(doc, {
            // startY: options.showAddress ? 41.5 : 25.5,
            startY: 25.5,
            theme: 'plain',
            margin: { left: 10, right: 10 },
            showHead: 'firstPage',
            showFoot: 'lastPage',
            tableLineWidth: 0,
            head: [['Num/Parc', 'Vencimento', 'Dias', 'Valor', 'Juros', 'Multas', 'Total']],
            body: this._getBodyValues(options.clientStatementList),
            foot: this._getFootValues(options.clientStatementList),
            headStyles: {
                font: 'courier',
                fontStyle: 'bold',
                fontSize: 10,
                lineWidth: 0,
                halign: 'center',
            },
            bodyStyles: {
                font: 'courier',
                fontStyle: 'normal',
                fontSize: 10,
                cellPadding: {
                    top: 2.8,
                    bottom: 0,
                },
                minCellHeight: 10,
                lineWidth: 0,
                halign: 'center',
            },
            footStyles: {
                font: 'courier',
                fontStyle: 'bold',
                fontSize: 10,
                cellPadding: {
                    top: 1,
                    bottom: 1,
                },
                lineWidth: 0,
                halign: 'center',
            },
            didDrawPage: function (data) {
                // Linha abaixo do cabeçalho
                doc.text(separatorEqual, 10, 34);
            },
            didDrawCell: function (data) {
                if (data.section === 'body') {
                    const isBeforeLastRow = (data.row.index < data.table.body.length - 1);
                    const isLastRow = (data.row.index == data.table.body.length - 1);

                    // Linha abaixo das linhas
                    if (isBeforeLastRow) {
                        doc
                            .setFont('courier', 'normal')
                            .setFontSize(7)
                            .text('- '.repeat(65), 10, data.cell.y + data.row.height);
                    }

                    // Linha abaixo da última linha
                    if (isLastRow && data.column.index === 0) {
                        doc
                            .setFont('courier', 'normal')
                            .setFontSize(10)
                            .text(separatorEqual, 10, data.cell.y + data.row.height)
                    }
                }
            }
        });

        /** Show pdf */
        const fileName: string = `relatorio_extrato_${moment().toString().replace('/', '_').replace(':', '_')}`
        doc.save(`${fileName}.pdf`);
    }

    private _getBodyValues(clientStatementList: FinancialClientStatementReportDTO[]): string[][] {
        if (!clientStatementList || clientStatementList.length === 0) {
            return [];
        }
        return clientStatementList.map(item => {
            return Object.keys(item).map((key) => {
                const value = (item as any)[key];
                if (['amount', 'amountNet', 'fine', 'interest'].includes(key)) {
                    return CurrencyUtil.transform(value, '1.2-2');
                }
                if (key === 'dueDate') {
                    return DateUtil.format(value, DateUtil.DATE_FORMAT);
                }
                return String(value);
            });
        });
    }

    private _getFootValues(clientStatementList: FinancialClientStatementReportDTO[]): string[][] {
        if (!clientStatementList || clientStatementList.length === 0) {
            return [];
        }
        const totals = clientStatementList.reduce((sum, e) => {
            sum.amount += e.amount || 0;
            sum.amountNet += e.amountNet || 0;
            sum.fine += e.fine || 0;
            sum.interest += e.interest || 0;
            return sum;
        }, { amount: 0, amountNet: 0, fine: 0, interest: 0, });

        const totalAmount = CurrencyUtil.transform(totals.amount, '1.2-2');
        const totalInterest = CurrencyUtil.transform(totals.interest, '1.2-2');
        const totalFine = CurrencyUtil.transform(totals.fine, '1.2-2');
        const totalAmountNet = CurrencyUtil.transform(totals.amountNet, '1.2-2');

        return [['', '', '', totalAmount, totalInterest, totalFine, totalAmountNet]];
    }

    private _getClientInfo(client: RegistrationClient): {
        name: string,
        cpfOrCnpj: string,
        street: string,
        city: string,
        zipCode: string,
        neighborhood: string,
        phones: string,
    } {
        const person = client.person;
        const address = !ArrayUtil.isEmpty(person.addressList)
            ? person.addressList.length > 0 ? person.addressList.find(e => e.useInInvoice) : person.addressList[0]
            : null;
        const phoneList = !ArrayUtil.isEmpty(person.phoneList)
            ? person.phoneList.slice(0, 3)
            : [];

        const name: string = StringUtil.isEmpty(person.fantasyName) ? person.name : person.fantasyName;

        let cpfOrCnpj: string = ''
        if (!ObjectUtil.isNull(person.legalPerson)) {
            cpfOrCnpj = !StringUtil.isEmpty(person.legalPerson.cnpj) ? StringUtil.format(person.legalPerson.cnpj, '00.000.000/0000-00') : '';
        } else if (!ObjectUtil.isNull(person.naturalPerson)) {
            cpfOrCnpj = !StringUtil.isEmpty(person.naturalPerson.cpf) ? StringUtil.format(person.naturalPerson.cpf, '000.000.000-00') : '';
        }

        const street: string = !ObjectUtil.isNull(address) ? address.street : '';
        const city: string = !ObjectUtil.isNull(address) ? address.city.name : '';
        const zipCode: string = !ObjectUtil.isNull(address) ? StringUtil.format(address.zipCode, '00000-000') : '';
        const neighborhood: string = !ObjectUtil.isNull(address) ? address.neighborhood : '';
        const fones: string = !ArrayUtil.isEmpty(phoneList) ? phoneList.join(', ') : '';

        return {
            name: name,
            cpfOrCnpj: cpfOrCnpj,
            street: street,
            city: city,
            zipCode: zipCode,
            neighborhood: neighborhood,
            phones: fones,
        }

    }

    private _getUserLogged(): void {
        try {
            const token: TokenDTO<OAuthUser> = this._authenticationService.token;
            if (this._authenticationService.isValidToken(token)) {
                this._user = token.user.name;
            }
        } catch (e) {
            throw new Error(e);
        }
    }

    private _getCompanyLogged(): void {
        try {
            const token: TokenDTO<OAuthUser> = this._authenticationService.token;
            if (this._authenticationService.isValidToken(token)) {
                this._companyService.getOne(token.user.currentCompany).toPromise().then(company => {
                    if (!ObjectUtil.isNull(company)) {
                        this._company = company.person.name;
                    }

                });
            }
        } catch (e) {
            throw new Error(e);
        }
    }

}
